import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiPackage, FiRefreshCcw, FiDownloadCloud, FiEdit2, FiEye, FiX } from "react-icons/fi";
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import { GET_PURCHASE_REPORT, GET_PURCHASE_ALL_DATA, BASE_URL, GET_ITEM_MASTER } from '../../utlilities/config';
import axios from 'axios';
import withAuth from '../../common/auth';
import { format } from 'date-fns';
import HighlightMetricsModal from './components/HighlightMetricsModal';

// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
            <p className="text-indigo-600 font-medium">Loading...</p>
        </div>
    </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
    const today = new Date();
    const todayFormatted = formatLocalDate(today);

    return filters.reduce((accumulator, filter) => {
        if (filter.type === 'date') {
            // Set both fromDate and toDate to today by default
            const dateValue = todayFormatted;
            return { ...accumulator, [filter.id]: dateValue };
        }

        if (filter.type === 'select') {
            const defaultValue = filter.options?.[0]?.value ?? '';
            return { ...accumulator, [filter.id]: defaultValue };
        }

        return { ...accumulator, [filter.id]: '' };
    }, {});
};

// Custom Select Styles
const customSelectStyles = {
    control: (base) => ({
        ...base,
        minHeight: '42px',
        height: '42px',
        fontSize: '14px',
        borderRadius: '0.5rem',
        border: '1px solid #e2e8f0',
        boxShadow: 'none',
        '&:hover': {
            border: '1px solid #cbd5e1',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 12px',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: '42px',
    }),
    menu: (base) => ({
        ...base,
        fontSize: '14px',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        zIndex: 9999,
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef2ff' : 'white',
        color: state.isSelected ? 'white' : '#374151',
        cursor: 'pointer',
        ':active': {
            backgroundColor: '#4f46e5',
            color: 'white',
        },
    }),
};

// Report configuration
const reportConfig = {
    filters: [
        { id: 'fromDate', type: 'date', label: 'From Date' },
        { id: 'toDate', type: 'date', label: 'To Date' },
    ],
    table: {
        columns: [
            { key: 'InvoiceNo', label: 'Invoice No' },
            { key: 'PurchaseRefNo', label: 'Purchase Ref No' },
            { key: 'PurchaseDate', label: 'Date' },
            { key: 'SupplierName', label: 'Supplier Name' },
            { key: 'NetAmount', label: 'Grand Total' },
            { key: 'TaxAmount', label: 'Tax Amount' },
        ],
    },
};

export const PurchaseItemWise = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [purchaseData, setPurchaseData] = useState([]);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [purchaseDetails, setPurchaseDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemsMaster, setItemsMaster] = useState([]);
    const token = localStorage.getItem('accessToken');
    const itemSearchDebounceRef = useRef(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(1000);

    // Initial load - fetch first 1000 items
    useEffect(() => {
        const fetchItems = async () => {
            try {
                const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
                    headers: {
                        'XApiKey': process.env.REACT_APP_API_KEY,
                        'Authorization': `Bearer ${token}`,
                        'PageNo': 1,
                        'PageSize': 1000,
                        'FilterText': '',
                        'FilterColumn': '0',
                        'FilterValue': '0',
                    },
                });

                if (response.status === 200) {
                    const masterData = response.data?.data?.masterData || [];
                    const pharmacyItems = masterData.filter(
                        (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
                    );
                    setItemsMaster(pharmacyItems);
                }
            } catch (error) {
                console.error('Failed to fetch items:', error);
            }
        };

        if (token) {
            fetchItems();
        }
    }, [token]);

    // Load items from API based on search text (for AsyncSelect)
    const loadItemOptions = (inputValue, callback) => {
        // Clear previous debounce timeout
        if (itemSearchDebounceRef.current) {
            clearTimeout(itemSearchDebounceRef.current);
        }

        // If input is less than 2 characters, return initial items
        if (!inputValue || inputValue.length < 2) {
            const options = (itemsMaster || []).map((item) => ({
                value: item.Item_No ?? item.ItemNo ?? item.id ?? null,
                label: item.Item_Name ?? item.ItemName ?? '',
            }));
            const filteredOptions = inputValue
                ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
                : options;
            callback(filteredOptions);
            return;
        }

        // Debounce API call
        itemSearchDebounceRef.current = setTimeout(async () => {
            try {
                const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
                    headers: {
                        'XApiKey': process.env.REACT_APP_API_KEY,
                        'Authorization': `Bearer ${token}`,
                        'PageNo': 1,
                        'PageSize': 1000,
                        'FilterText': inputValue,
                        'FilterColumn': '0',
                        'FilterValue': '0',
                    },
                });

                if (response.status === 200) {
                    const masterData = response.data?.data?.masterData || [];
                    const pharmacyItems = masterData.filter(
                        (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
                    );

                    const options = pharmacyItems.map((item) => ({
                        value: item.Item_No ?? item.ItemNo ?? item.id ?? null,
                        label: item.Item_Name ?? item.ItemName ?? '',
                        ...item,
                    }));

                    const filteredOptions = inputValue
                        ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
                        : options;
                    callback(filteredOptions);
                } else {
                    callback([]);
                }
            } catch (error) {
                console.error('Failed to fetch items:', error);
                callback([]);
            }
        }, 300); // 300ms debounce
    };

    // Get selected item option for AsyncSelect value prop
    const getSelectedItemOption = (itemId) => {
        if (!itemId) return null;

        // First, check if selectedItem exists and matches
        if (selectedItem) {
            const selectedValue = selectedItem.Item_No ?? selectedItem.ItemNo ?? selectedItem.id ?? selectedItem.value;
            if (selectedValue === itemId || String(selectedValue) === String(itemId)) {
                return {
                    value: selectedValue,
                    label: selectedItem.Item_Name ?? selectedItem.ItemName ?? selectedItem.label ?? '',
                };
            }
        }

        // Fallback: search in itemsMaster
        const item = itemsMaster.find(i => {
            const itemNo = i.Item_No ?? i.ItemNo ?? i.id;
            return itemNo === itemId || String(itemNo) === String(itemId);
        });

        if (item) {
            return {
                value: item.Item_No ?? item.ItemNo ?? item.id ?? null,
                label: item.Item_Name ?? item.ItemName ?? '',
            };
        }

        return null;
    };

    // Cleanup debounce timeout on unmount
    useEffect(() => {
        return () => {
            if (itemSearchDebounceRef.current) {
                clearTimeout(itemSearchDebounceRef.current);
            }
        };
    }, []);

    // Handle item change
    const handleItemChange = (selectedOption) => {
        if (!selectedOption) {
            setSelectedItem(null);
            return;
        }

        const item = selectedOption;

        // Add to itemsMaster if not already present
        if (item) {
            const itemNo = item.Item_No ?? item.ItemNo ?? item.id ?? item.value;
            const existingItem = itemsMaster.find(i => {
                const iNo = i.Item_No ?? i.ItemNo ?? i.id;
                return iNo === itemNo || String(iNo) === String(itemNo);
            });

            if (!existingItem) {
                setItemsMaster(prev => [...prev, item]);
            }
        }

        setSelectedItem(selectedOption);
    };

    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    const handleRunReport = () => {
        if (!selectedItem) {
            toast.error('Please select an item', { autoClose: 2000 });
            return;
        }
        getPurchaseReport();
    };

    const handleExport = () => {
        toast.info('Exporting to CSV...', { autoClose: 1000 });
        // Simulate export
        setTimeout(() => {
            toast.success('CSV exported successfully', { autoClose: 1000 });
        }, 1500);
    };

    const handleEdit = (row) => {
        const editData = {
            invoiceNo: row.InvoiceNo,
            sequence: row.Sequence || 2024,
            editNo: row.EditNo !== undefined ? row.EditNo : 0,
        };

        if (!editData.invoiceNo || !editData.sequence) {
            toast.error('Invoice number not available', { autoClose: 1500 });
            return;
        }

        if (tabContext && tabContext.openTab) {
            tabContext.openTab(
                `purchaseedit/${encodeURIComponent(editData.invoiceNo)}/${encodeURIComponent(editData.sequence)}/${editData.editNo}`,
                `Purchase Edit - ${editData.invoiceNo}`
            );
        }
    };

    // Pagination calculations
    const totalPages = Math.ceil(purchaseData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = purchaseData.slice(startIndex, endIndex);

    // Reset to page 1 when filters change or new data is loaded
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.fromDate, filters.toDate, selectedItem, purchaseData.length]);

    const handleViewDetails = async (row) => {
        const invoiceNo = row.InvoiceNo;
        const sequence = row.Sequence || 2024;
        const editNo = row.EditNo !== undefined ? row.EditNo : 0;

        if (!invoiceNo) {
            toast.error('Invoice number not available', { autoClose: 1500 });
            return;
        }

        setLoadingDetails(true);
        setShowDetailsModal(true);

        try {

            const response = await axios.get(`${BASE_URL}${GET_PURCHASE_ALL_DATA}`, {
                params: {
                    _sequence: sequence,
                    invoiceNo: invoiceNo,
                    editNo: editNo,
                    docType: 'Pharmacy',
                },
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.status === 200 && response.data?.isSucess) {
                setPurchaseDetails(response.data.data);
            } else {
                toast.error('Failed to load purchase details', { autoClose: 1500 });
                setShowDetailsModal(false);
            }
        } catch (error) {
            console.error('Failed to fetch purchase details:', error);
            toast.error('Failed to load purchase details', { autoClose: 1500 });
            setShowDetailsModal(false);
        } finally {
            setLoadingDetails(false);
        }
    };

    const formatDateString = (value) => {
        if (!value) return '-';
        try {
            const date = new Date(value);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return value;
        }
    };

    const formatExpiryDate = (value) => {
        if (!value) return '-';
        try {
            const date = new Date(value);
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = String(date.getFullYear()).slice(-2);
            return `${month}/${year}`;
        } catch {
            return value;
        }
    };

    const getPurchaseReport = useCallback(async () => {
        if (!selectedItem) {
            toast.error('Please select an item', { autoClose: 2000 });
            return;
        }

        setLoading(true);
        try {
            // Convert date strings to Date objects and format as YYYY-MM-DD
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;

            // Format date as YYYY-MM-DD
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const itemNo = selectedItem.Item_No ?? selectedItem.ItemNo ?? selectedItem.id ?? selectedItem.value;

            const response = await axios.get(`${BASE_URL}${GET_PURCHASE_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'rpType': 'Item',
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'searchKey': itemNo,
                    'itemNo': 0,
                },
            });
            if (response.status === 200) {
                const data = response.data.data || [];
                // Sort by InvoiceNo in descending order
                const sortedData = data.sort((a, b) => {
                    const invoiceA = a.InvoiceNo || a.PurchaseRefNo || '';
                    const invoiceB = b.InvoiceNo || b.PurchaseRefNo || '';
                    // Convert to number if possible, otherwise compare as strings
                    const numA = parseInt(invoiceA) || 0;
                    const numB = parseInt(invoiceB) || 0;
                    if (numA !== 0 || numB !== 0) {
                        return numB - numA; // Descending order
                    }
                    return invoiceB.localeCompare(invoiceA); // Descending order for strings
                });
                setPurchaseData(sortedData);
                console.log('Purchase item wise report:', sortedData);
            } else {
                toast.error('Failed to fetch purchase report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch purchase report:', error);
            toast.error('Failed to fetch purchase report', { autoClose: 1000 });
        }
        finally {
            setLoading(false);
        }
    }, [filters, token, selectedItem]);

    return (
        <div className="flex h-full bg-background">
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />

            {loading && <Spinner />}

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div className="min-w-0 flex-1">
                            {!embedded && (
                                <div className="mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                        Purchase Reports
                                    </p>
                                    <h1 className="text-lg font-bold text-slate-800">
                                        Purchase Item Wise
                                    </h1>
                                </div>
                            )}
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-3">
                                {reportConfig.filters.map((filter) => {
                                    if (filter.type === 'date') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700 shrink-0 w-[9.5rem]">
                                                {filter.label}
                                                <input
                                                    type="date"
                                                    value={filters[filter.id] ?? ''}
                                                    min={filter.id === 'toDate' ? filters.fromDate : undefined}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                />
                                            </label>
                                        );
                                    }
                                    return null;
                                })}
                                <label className="flex flex-col gap-1 text-xs font-medium text-slate-700 flex-1 min-w-0 w-full">
                                    Item
                                    <div className="[&>div]:!h-auto">
                                        <AsyncSelect
                                            loadOptions={loadItemOptions}
                                            isSearchable
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            defaultOptions={true}
                                            cacheOptions={true}
                                            value={getSelectedItemOption(selectedItem?.value)}
                                            onChange={handleItemChange}
                                            noOptionsMessage={({ inputValue }) =>
                                                inputValue && inputValue.length < 2
                                                    ? 'Type at least 2 characters to search'
                                                    : 'No items found'
                                            }
                                            loadingMessage={() => 'Searching items...'}
                                            styles={customSelectStyles}
                                            placeholder="Select Item"
                                        />
                                    </div>
                                </label>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={handleRunReport}
                                disabled={!selectedItem}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FiRefreshCcw size={16} />
                                Run report
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilters(buildInitialFilters(reportConfig.filters));
                                    setSelectedItem(null);
                                    setPurchaseData([]);
                                }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-3 flex-1 min-h-0 px-3 pb-3">

                        <HighlightMetricsModal
                            isOpen={isMetricsModalOpen}
                            onOpen={() => setIsMetricsModalOpen(true)}
                            onClose={() => setIsMetricsModalOpen(false)}
                        >
                            <div className="grid gap-4 md:grid-cols-3">
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Bills
                                    </p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">{purchaseData.length}</p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Amount
                                    </p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">
                                        ₹{purchaseData.reduce((sum, item) => sum + (parseFloat(item.NetAmount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Tax
                                    </p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">
                                        ₹{purchaseData.reduce((sum, item) => sum + (parseFloat(item.TaxAmount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </HighlightMetricsModal>

                        {/* Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-4">
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{startIndex + 1}-{Math.min(endIndex, purchaseData.length)}</span> of <span className="font-semibold text-slate-700">{purchaseData.length}</span> datas for this report.
                                    </p>
                                    {/* Items per page selector */}
                                    {/* <div className="flex items-center gap-2">
                                        <label className="text-xs text-slate-600">Items per page:</label>
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                                        >
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                        </select>
                                    </div> */}
                                </div>
                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${currentPage === pageNum
                                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                                            : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="mt-4 flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                                <table className="min-w-full divide-y divide-slate-200 text-sm text-slate-700">
                                    <thead className="bg-gray-200 sticky top-0 z-10">
                                        <tr>
                                            {reportConfig.table.columns.map((column) => (
                                                <th
                                                    key={column.key}
                                                    scope="col"
                                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                                                >
                                                    {column.label}
                                                </th>
                                            ))}
                                            <th
                                                scope="col"
                                                className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500"
                                            >
                                                Action
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {purchaseData.length > 0 ? (
                                            paginatedData.map((row, index) => (
                                                <tr key={`${row.PurchaseRefNo}-${index}`} className="hover:bg-indigo-50/40 transition">
                                                    {reportConfig.table.columns.map((column) => {
                                                        let cellValue = row[column.key];

                                                        // Format specific columns
                                                        if (column.key === 'PurchaseDate') {
                                                            cellValue = new Date(cellValue).toLocaleDateString('en-GB', {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric'
                                                            });
                                                        } else if (['NetAmount', 'TaxAmount', 'DiscountAmount', 'BalanceDue'].includes(column.key)) {
                                                            cellValue = `₹${parseFloat(cellValue || 0).toFixed(2)}`;
                                                        } else if (column.key === 'PaymentMode') {
                                                            // Capitalize first letter of payment mode
                                                            cellValue = cellValue ? cellValue.charAt(0).toUpperCase() + cellValue.slice(1) : '-';
                                                        } else if (column.key === 'SupplierName' && (!cellValue || cellValue === null)) {
                                                            cellValue = '-';
                                                        }

                                                        return (
                                                            <td key={column.key} className="whitespace-nowrap px-4 py-3 text-slate-700">
                                                                {cellValue}
                                                            </td>
                                                        );
                                                    })}
                                                    <td className="px-4 py-3 text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <button
                                                                onClick={() => handleViewDetails(row)}
                                                                className="inline-flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                title="View Details"
                                                            >
                                                                <FiEye size={18} />
                                                            </button>
                                                            {/* <button
                                                                onClick={() => handleEdit(row)}
                                                                className="inline-flex items-center justify-center p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                                title="Edit Purchase"
                                                            >
                                                                <FiEdit2 size={18} />
                                                            </button> */}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={reportConfig.table.columns.length + 1} className="px-4 py-8 text-center text-slate-500">
                                                    {selectedItem ? 'No data available for the selected period' : 'Please select an item to view purchase reports'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {/* Bottom Pagination Controls */}
                            {purchaseData.length > 0 && totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-slate-200">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Previous
                                    </button>
                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 7) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 4) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 3) {
                                                pageNum = totalPages - 6 + i;
                                            } else {
                                                pageNum = currentPage - 3 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${currentPage === pageNum
                                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                                        : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <span className="text-sm text-slate-600 px-2">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </section>
                    </div>
                </main>
            </div>

            {/* Purchase Details Modal */}
            {showDetailsModal && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-2xl w-[95%] max-w-6xl max-h-[90vh] flex flex-col">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-slate-800">Purchase Invoice Details</h2>
                            <button
                                onClick={() => {
                                    setShowDetailsModal(false);
                                    setPurchaseDetails(null);
                                }}
                                className="p-2 text-black hover:text-black hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <FiX size={20} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {loadingDetails ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-indigo-600"></div>
                                </div>
                            ) : purchaseDetails ? (
                                <div className="space-y-6">
                                    {/* Header Information */}
                                    {purchaseDetails.invHeader?.[0] && (
                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                            <h3 className="text-lg font-semibold text-slate-800 mb-4">Purchase Information</h3>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Supplier</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">{purchaseDetails.invHeader[0].SupplierName || '-'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Invoice Number</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">{purchaseDetails.invHeader[0].PurchaseRefNo || '-'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Invoice Date</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">{formatDateString(purchaseDetails.invHeader[0].InvoiceDate || purchaseDetails.invHeader[0].PurchaseDate)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Payment Mode</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1 capitalize">{purchaseDetails.invHeader[0].PaymentMode || '-'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Due Date</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">{formatDateString(purchaseDetails.invHeader[0].DueDate) || '-'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Remarks</p>
                                                    <p className="text-sm font-medium text-slate-800 mt-1">{purchaseDetails.invHeader[0].Remarks || '-'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Line Items */}
                                    {purchaseDetails.invLineDetail && purchaseDetails.invLineDetail.length > 0 && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-slate-800 mb-4">Product Details</h3>
                                            <div className="overflow-x-auto rounded-lg border border-gray-200">
                                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                    <thead className="bg-gray-100">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-black">No.</th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-black">Product Name</th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-black">Batch</th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-black">Packing</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Qty</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Free</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Rate</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">MRP</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Amount</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Discount</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">GST%</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Tax</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-black">Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {purchaseDetails.invLineDetail.map((line, index) => {
                                                            const batchDetail = purchaseDetails.invBatchDetails?.find(
                                                                (b) => b.Item_No === line.ItemNo && b.BatchCode === line.BatchCode
                                                            );
                                                            const packageSize = line.PackageSize || line.Packing || 1;
                                                            const totalUnits = line.Quantity || 0;
                                                            const freeUnits = line.FOC || 0;
                                                            const paidUnits = totalUnits - freeUnits;
                                                            const qty = packageSize > 0 ? paidUnits / packageSize : 0;
                                                            const foc = packageSize > 0 ? freeUnits / packageSize : 0;
                                                            const unitRate = line.Price || 0;
                                                            const rate = unitRate * packageSize;
                                                            const unitMrp = line.MRP || 0;
                                                            const mrp = unitMrp * packageSize;

                                                            return (
                                                                <tr key={index} className="hover:bg-gray-50">
                                                                    <td className="px-4 py-3 text-black">{index + 1}</td>
                                                                    <td className="px-4 py-3 font-medium text-black">{line.ItemName || '-'}</td>
                                                                    <td className="px-4 py-3 text-black">{line.BatchCode || '-'}</td>
                                                                    <td className="px-4 py-3 text-black">{packageSize}</td>
                                                                    <td className="px-4 py-3 text-right text-black">{qty.toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right text-black">{foc.toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right text-black">₹{rate.toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right text-black">₹{mrp.toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right text-black">₹{parseFloat(line.Total || 0).toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right text-black">
                                                                        {line.DiscountPercentage ? `${line.DiscountPercentage}%` : `₹${parseFloat(line.DiscountAmount || 0).toFixed(2)}`}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right text-black">{line.TaxCode || '0'}%</td>
                                                                    <td className="px-4 py-3 text-right text-black">₹{parseFloat(line.TaxAmount || 0).toFixed(2)}</td>
                                                                    <td className="px-4 py-3 text-right font-semibold text-black">₹{parseFloat(line.NetTotal || 0).toFixed(2)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Summary */}
                                    {purchaseDetails.invHeader?.[0] && (
                                        <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                                            <h3 className="text-lg font-semibold text-slate-800 mb-4">Summary</h3>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Sub Total</p>
                                                    <p className="text-lg font-bold text-slate-800 mt-1">₹{parseFloat(purchaseDetails.invHeader[0].Total || 0).toFixed(2)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Discount</p>
                                                    <p className="text-lg font-bold text-red-600 mt-1">₹{parseFloat(purchaseDetails.invHeader[0].DiscountAmount || 0).toFixed(2)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Tax Amount</p>
                                                    <p className="text-lg font-bold text-slate-800 mt-1">₹{parseFloat(purchaseDetails.invHeader[0].TaxAmount || 0).toFixed(2)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-black uppercase">Grand Total</p>
                                                    <p className="text-xl font-bold text-indigo-600 mt-1">₹{parseFloat(purchaseDetails.invHeader[0].NetAmount || 0).toFixed(2)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-12 text-black">No details available</div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-end p-4 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowDetailsModal(false);
                                    setPurchaseDetails(null);
                                }}
                                className="px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default withAuth(PurchaseItemWise);

